import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import Grid from '@mui/material/Grid2';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { useTranslation } from 'react-i18next';
import ClientService from '../../services/ClientService';
import { errorMessage, fieldErrors } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const EMPTY = { type: 'PERSON', name: '', idNumber: '', phone: '', email: '', address: '', city: '', notes: '' };

/** Create or edit a client. `initialName` pre-fills the name when created from the client picker. */
export default function ClientFormDialog({ open, client, initialName, onClose, onSaved }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Initialise only when the dialog opens, so later prop changes never wipe what the user typed.
    useEffect(() => {
        if (!open) return;
        setErrors({});
        setForm(client ? { ...EMPTY, ...Object.fromEntries(Object.entries(client).map(([k, v]) => [k, v ?? ''])) }
                       : { ...EMPTY, name: initialName || '' });
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k) => (e) => {
        setForm(f => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors(x => ({ ...x, [k]: undefined }));
    };

    const save = async () => {
        if (!form.name.trim()) { setErrors({ name: t('validation.required') }); return; }
        setSaving(true);
        try {
            const payload = { type: form.type, name: form.name, idNumber: form.idNumber, phone: form.phone, email: form.email, address: form.address, city: form.city, notes: form.notes };
            const saved = client ? await ClientService.update(client.id, payload) : await ClientService.create(payload);
            showToast(client ? t('clients.toast.updated') : t('clients.toast.created'), 'success');
            onSaved(saved);
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) setErrors(fe);
            else showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    const field = (k, label, props = {}) => (
        <TextField fullWidth size="small" label={label} value={form[k]} onChange={set(k)}
                   error={!!errors[k]} helperText={errors[k]} {...props} />
    );

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            <DialogTitle sx={{ fontWeight: 700 }}>{client ? t('clients.edit') : t('clients.new')}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, mt: 0.5 }}>
                    <ToggleButtonGroup exclusive size="small" value={form.type} onChange={(_, v) => v && setForm(f => ({ ...f, type: v }))}>
                        <ToggleButton value="PERSON" sx={{ px: 2, gap: 0.75 }}><PersonOutlineIcon fontSize="small" />{t('clients.type.PERSON')}</ToggleButton>
                        <ToggleButton value="COMPANY" sx={{ px: 2, gap: 0.75 }}><ApartmentOutlinedIcon fontSize="small" />{t('clients.type.COMPANY')}</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 8 }}>{field('name', form.type === 'COMPANY' ? t('clients.fields.companyName') : t('clients.fields.fullName'), { autoFocus: true, required: true })}</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>{field('idNumber', form.type === 'COMPANY' ? t('clients.fields.businessNumber') : t('clients.fields.personalNumber'))}</Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>{field('phone', t('clients.fields.phone'), { type: 'tel' })}</Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>{field('email', t('clients.fields.email'), { type: 'email' })}</Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>{field('address', t('clients.fields.address'))}</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>{field('city', t('clients.fields.city'))}</Grid>
                    <Grid size={12}>{field('notes', t('clients.fields.notes'), { multiline: true, minRows: 2 })}</Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} disableElevation>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
